import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTemplates, createTemplate, duplicateTemplate, deleteTemplate, updateTemplate } from '../../services/eligibilityService';
import { invalidateCache } from '../../utils/apiDataCache';
import ConfirmModal from '../common/ConfirmModal';
import Loader from '../common/Loader';
import Alert from '../common/Alert';

export default function EligibilityTemplateList() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, total_pages: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', disability_category: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchTemplates = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getTemplates(true, page, pagination.limit);
      const payload = res.data.data || {};
      setTemplates(payload.templates || []);
      setPagination({
        total: payload.total || 0,
        page: payload.page || page,
        limit: payload.limit || pagination.limit,
        total_pages: payload.total_pages || 0,
      });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to load templates.' });
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => { fetchTemplates(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.total_pages) return;
    fetchTemplates(page);
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) {
      setAlert({ type: 'error', message: 'Template name is required.' });
      return;
    }
    setCreating(true);
    try {
      const body = { name: createForm.name.trim() };
      if (createForm.disability_category.trim()) body.disability_category = createForm.disability_category.trim();
      if (createForm.description.trim()) body.description = createForm.description.trim();
      const res = await createTemplate(body);
      invalidateCache('eligibility-templates:');
      setShowCreateModal(false);
      setCreateForm({ name: '', disability_category: '', description: '' });
      setAlert({ type: 'success', message: 'Template created successfully.' });
      navigate(`/admin/eligibility-templates/${res.data.data.id}`);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to create template.' });
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateTarget) return;
    setDuplicating(true);
    try {
      const res = await duplicateTemplate(duplicateTarget.id);
      invalidateCache('eligibility-templates:');
      setDuplicateTarget(null);
      setAlert({ type: 'success', message: 'Template duplicated.' });
      navigate(`/admin/eligibility-templates/${res.data.data.id}`);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to duplicate template.' });
    } finally {
      setDuplicating(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!statusTarget) return;
    setToggling(true);
    try {
      if (statusTarget.is_active) {
        await deleteTemplate(statusTarget.id);
        setAlert({ type: 'success', message: 'Template deactivated.' });
      } else {
        await updateTemplate(statusTarget.id, { is_active: true });
        setAlert({ type: 'success', message: 'Template activated.' });
      }
      invalidateCache('eligibility-templates:');
      setStatusTarget(null);
      fetchTemplates(pagination.page);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to update template status.' });
    } finally {
      setToggling(false);
    }
  };

  const handleDeletePermanent = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteTemplate(deleteTarget.id, true);
      invalidateCache('eligibility-templates:');
      setDeleteTarget(null);
      setAlert({ type: 'success', message: 'Template deleted permanently.' });
      fetchTemplates(pagination.page);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to delete template.' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading && templates.length === 0) return <Loader fullScreen text="Loading templates..." />;

  return (
    <div className="min-w-0 space-y-5">
      {alert && (
        <div className="fixed top-20 left-4 right-4 sm:left-auto sm:right-4 z-50 sm:w-80 sm:max-w-sm">
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        </div>
      )}

      <ConfirmModal
        open={!!duplicateTarget}
        title="Duplicate Template"
        message={`Create a copy of "${duplicateTarget?.name}"? You will be taken to edit the new template.`}
        onConfirm={handleDuplicate}
        onCancel={() => setDuplicateTarget(null)}
        confirmText="Duplicate"
        variant="info"
        loading={duplicating}
      />

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete Template"
        message={`Permanently delete "${deleteTarget?.name}"? This cannot be undone.`}
        onConfirm={handleDeletePermanent}
        onCancel={() => setDeleteTarget(null)}
        confirmText="Delete"
        variant="danger"
        loading={deleting}
      />

      <ConfirmModal
        open={!!statusTarget}
        title={statusTarget?.is_active ? 'Deactivate Template' : 'Activate Template'}
        message={
          statusTarget?.is_active
            ? `"${statusTarget?.name}" will be deactivated and hidden from new eligibility checklists.`
            : `"${statusTarget?.name}" will be activated and available for new eligibility checklists.`
        }
        onConfirm={handleToggleStatus}
        onCancel={() => setStatusTarget(null)}
        confirmText={statusTarget?.is_active ? 'Deactivate' : 'Activate'}
        variant={statusTarget?.is_active ? 'danger' : 'info'}
        loading={toggling}
      />

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Create New Template</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Name *</label>
                <input
                  value={createForm.name}
                  onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Autism Eligibility Checklist"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Disability Category</label>
                <input
                  value={createForm.disability_category}
                  onChange={(e) => setCreateForm((p) => ({ ...p, disability_category: e.target.value }))}
                  placeholder="e.g. autism, sld, speech..."
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  placeholder="Brief description of the template..."
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-5">
              <button type="button" onClick={handleCreate} disabled={creating} className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50">
                {creating ? 'Creating...' : 'Create'}
              </button>
              <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight">Eligibility Templates</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">Manage eligibility compliance checklist templates</p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
          Create Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-10 text-center">
          <p className="text-gray-500 dark:text-slate-400">No templates found.</p>
        </div>
      ) : (
        <div className="grid gap-3 min-w-0">
          {templates.map((t) => (
            <div
              key={t.id}
              className={`bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-3 sm:p-5 overflow-hidden hover:border-indigo-300 dark:hover:border-indigo-600 transition-colors ${!t.is_active ? 'opacity-75' : ''}`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 min-w-0">
                <div
                  className="min-w-0 flex-1 cursor-pointer"
                  onClick={() => navigate(`/admin/eligibility-templates/${t.id}`)}
                >
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white break-words leading-snug">
                    {t.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    {t.is_default && (
                      <span className="px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-[10px] font-semibold uppercase whitespace-nowrap">
                        Default
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase whitespace-nowrap ${
                      t.is_active
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                    }`}>
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500 dark:text-slate-400">
                    {t.disability_category && <span className="capitalize break-all">{t.disability_category}</span>}
                    <span className="whitespace-nowrap">{t.section_count} sections</span>
                    <span className="whitespace-nowrap">{t.field_count} fields</span>
                  </div>
                  {t.description && (
                    <p className="text-xs text-gray-400 dark:text-slate-500 mt-1.5 line-clamp-2 sm:line-clamp-none sm:truncate leading-relaxed">
                      {t.description}
                    </p>
                  )}
                </div>

                <div
                  className="flex items-center justify-end gap-1 flex-shrink-0 border-t border-gray-100 dark:border-slate-700 pt-2 sm:border-t-0 sm:pt-0"
                  onClick={(e) => e.stopPropagation()}
                >
                <button
                  type="button"
                  onClick={() => navigate(`/admin/eligibility-templates/${t.id}`)}
                  title="Edit"
                  className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                </button>
                <button
                  type="button"
                  onClick={() => setDuplicateTarget(t)}
                  title="Duplicate"
                  className="p-2 rounded-lg text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                </button>
                {!t.is_default && (
                  <>
                    {t.is_active ? (
                      <button
                        type="button"
                        onClick={() => setStatusTarget(t)}
                        title="Deactivate"
                        className="p-2 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setStatusTarget(t)}
                        title="Activate"
                        className="p-2 rounded-lg text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(t)}
                      title="Delete permanently"
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </>
                )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {pagination.total_pages > 1 && (
        <div className="mt-2 sm:mt-4 px-1 py-3 border-t border-gray-100 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 text-center sm:text-left">
            Showing{' '}
            <span className="font-medium text-gray-700 dark:text-slate-300">
              {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
            </span>{' '}
            of <span className="font-medium text-gray-700 dark:text-slate-300">{pagination.total}</span>
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
              let pageNum;
              if (pagination.total_pages <= 5) pageNum = i + 1;
              else if (pagination.page <= 3) pageNum = i + 1;
              else if (pagination.page >= pagination.total_pages - 2) pageNum = pagination.total_pages - 4 + i;
              else pageNum = pagination.page - 2 + i;
              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => handlePageChange(pageNum)}
                  disabled={loading}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors ${
                    pageNum === pagination.page
                      ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                      : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.total_pages || loading}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
